// A gate for async work where only the most recent call's result may be used.
//
// The subscriptions dialog fetches the selected user's cars. Selecting user A
// and then user B lets A's response arrive after B's, which would list A's cars
// under B's name — and the form would submit a subscription for B carrying a
// car that belongs to A. Nothing in the response says which selection asked for
// it, so the caller has to track that itself.
//
// Each run takes a ticket. When the task settles, its result is marked `stale`
// unless that ticket is still the most recent one issued. A stale result must
// touch no state at all — not the data, and not the loading flag, which a newer
// in-flight request still owns.
//
// Never rejects: a task that throws resolves to `{ stale, ok: false, error }`.
// The caller then handles "failed" and "superseded" on one code path and
// cannot leave a spinner running by forgetting a catch.
export function latestOnly() {
  let latestTicket = 0;

  return async function run(task) {
    const ticket = ++latestTicket;
    try {
      // Await into a const FIRST. Collapsing this into
      // `return { stale: ..., ok: true, value: await task() }` silently breaks
      // the gate on the success path: object properties evaluate left to right,
      // so `stale` would be computed before the task settles — back when this
      // ticket was still the latest one — and every successful result would
      // report `stale: false`. Two of the tests below catch exactly that.
      const value = await task();
      return { stale: ticket !== latestTicket, ok: true, value };
    } catch (error) {
      // `ok` rather than a truthiness check on `error`: a task may reject with
      // no reason at all (`Promise.reject()`), or with `null`. A caller testing
      // `if (result.error)` would read that as success and then dereference a
      // `value` that was never set.
      return { stale: ticket !== latestTicket, ok: false, error };
    }
  };
}
